import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useLocation } from 'react-router';
import { useChartDataOptionSelected } from 'components/Dashboard/Analytics/ui/MentionsByTopic/ChartDataTable';
import useModifyAnalyticsState from 'components/Dashboard/Analytics/useModifyAnalyticsState';
import {
  chartDataOptions as mainChartDataOptions,
  createIncludeRepostsOption,
} from 'components/Dashboard/Analytics/createAnalyticsState';
import { Portal } from 'react-portal';
import getAppSite from 'utils/getAppSite';

const createChartDataOptions = ({ chartType, tweetsDateInterval }) => {
  let options = [...mainChartDataOptions];
  if (chartType === 'Tweets' && !createIncludeRepostsOption({ tweetsDateInterval })) {
    const contributionTypeOptionPosition = options?.findIndex((item) => item?.name === 'Contribution type');
    options.splice(contributionTypeOptionPosition, 1);
  }
  return options;
};

const ChartOptionsButton = (props) => {
  const { isLoading, scrollBarPosition, chartType, analyticsState } = props;
  const { tweetsDateInterval } = analyticsState;
  const [openOptions, setOpenOptions] = useState(false);
  const [positions, setPositions] = useState({ top: 0, left: 0, opacity: 0 });
  const { changeChartData } = useModifyAnalyticsState();
  const filtersElement = useRef(null);
  const optionsElement = useRef(null);
  const hideFilters = () => {
    setOpenOptions(false);
  };

  const location = useLocation();

  const { chartOptionName } = useChartDataOptionSelected();
  const sentimentChanges = location.pathname.includes('/analytics/sentiment-changes');

  const createOptionsName = () => {
    if (chartOptionName === 'Contribution type') {
      return 'type';
    } else if (chartOptionName === 'Parliamentary position') {
      return 'position';
    } else {
      return getAppSite() === 'usa' && chartOptionName !== 'Overall' ? chartOptionName : chartOptionName.toLowerCase();
    }
  };

  useEffect(() => {
    if (!!scrollBarPosition) {
      setOpenOptions(false);
    }
  }, [scrollBarPosition]);

  const chartDataOptions = createChartDataOptions({ chartType, tweetsDateInterval });

  useLayoutEffect(() => {
    if (!openOptions) {
      setPositions((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const filtersRect = filtersElement.current?.getBoundingClientRect();
    const optionsRect = optionsElement.current?.getBoundingClientRect();

    if (!filtersRect || !optionsRect || typeof optionsRect.height !== 'number') {
      setPositions((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const { top, height, left } = filtersRect;
    const optionsHeight = optionsRect.height;

    let nextPositions;
    if (sentimentChanges) {
      if (top - optionsHeight < 0) {
        nextPositions = { top: height + 10, left };
      } else {
        nextPositions = { top: top - optionsHeight, left };
      }
    } else {
      nextPositions = { top: top + height + 10, left };
    }

    setPositions({ ...nextPositions, opacity: 1 });
  }, [openOptions, sentimentChanges]);

  return (
    <div className='position-relative' ref={filtersElement}>
      <button
        className={`general-button filter-item-button section-filter-button data-option-filter-button ${
          sentimentChanges ? '' : 'action'
        }`}
        onClick={() => {
          setOpenOptions(!openOptions);
        }}
      >
        <span
          className={`dropdown-item-element ${openOptions ? 'open' : ''} nowrap-item ${
            sentimentChanges ? 'title-h5-m' : ''
          }`}
        >
          {chartOptionName !== 'Overall' ? 'by' : ''} {createOptionsName()}
        </span>
      </button>
      {openOptions && (
        <Portal node={document && document.querySelector('body')}>
          <div className='general-close-background z-extra-max' onClick={() => setOpenOptions(false)} />
          <div
            className={`login-navbar py-2 trends-chart-data-options position-fixed z-extra-max`}
            style={{
              top: `${positions.top}px`,
              width: '258px',
              left: `${positions.left + 8}px`,
              opacity: positions.opacity,
            }}
            ref={optionsElement}
          >
            {chartDataOptions
              ?.filter((item) => !item?.graphsToShow || item?.graphsToShow?.includes(chartType))
              ?.map((item, index) => {
                const { name } = item;
                return (
                  <button
                    className='settings-button setting-item general-button d-block text-left w-100 px-4 position-relative'
                    key={`date-${normalize(name)}${index}`}
                    onClick={() => {
                      if (!isLoading) {
                        changeChartData({ chartDataOption: name });
                        hideFilters();
                      }
                    }}
                  >
                    {name !== 'Overall' ? 'by' : ''}{' '}
                    {getAppSite() === 'usa' && name !== 'Overall' ? name : name.toLowerCase()}
                    {name === chartOptionName && <span className='icon-tick topic-selected-mark paragraph-p4' />}
                  </button>
                );
              })}
          </div>
        </Portal>
      )}
    </div>
  );
};

export { createChartDataOptions };
export default ChartOptionsButton;
